import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { ProbateStatusItem } from '../Models/ProbateStatusItem';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-probate-status-report',
  templateUrl: './probate-status-report.component.html',
  styleUrls: ['./probate-status-report.component.css'],
  standalone: false
})
export class ProbateStatusReportComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = [
    'probateStatus', 'deceasedName', 'probateId', 'dateOfProbate',
    'currentOwner', 'reportingUser', 'reportingCompleteDate',
    'verificationUser', 'verificationCompleteDate',
    'firstClientEmailedDate', 'firstInvoiceCreatedDate',
    'ageInDays', 'reportingTimeFromIssueInDays', 'verificationTimeFromReportingInDays',
    'totalBequestCount', 'pecuniaryBequestCount', 'residualBequestCount',
    'effectBequestCount', 'hadsBequestCount',
    'aiCharitableCount', 'reportedClientCharitableCount', 'reportedNonClientCharitableCount',
    'namedSubscriptionCountThatThisTriggers', 'csvNamesOfNamedSubscriptionCountThatThisTriggers'
  ];

  isLoading = false;
  allRecords: ProbateStatusItem[] = [];
  dataSource = new MatTableDataSource<ProbateStatusItem>([]);

  pageSize = 50;

  dateFrom: Date;
  dateTo: Date;

  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {
    const now = new Date();
    this.dateTo = now;
    this.dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  }

  ngOnInit() {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loadData();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (item, column) => {
      switch (column) {
        case 'probateStatus': return item.probateStatus ?? '';
        case 'deceasedName': return item.deceasedName ?? '';
        case 'probateId': return item.probateId;
        case 'dateOfProbate': return item.dateOfProbate ?? '';
        case 'currentOwner': return item.currentOwner ?? '';
        case 'reportingUser': return item.reportingUser ?? '';
        case 'reportingCompleteDate': return item.reportingCompleteDate ?? '';
        case 'verificationUser': return item.verificationUser ?? '';
        case 'verificationCompleteDate': return item.verificationCompleteDate ?? '';
        case 'firstClientEmailedDate': return item.firstClientEmailedDate ?? '';
        case 'firstInvoiceCreatedDate': return item.firstInvoiceCreatedDate ?? '';
        case 'ageInDays': return item.ageInDays ?? 0;
        case 'reportingTimeFromIssueInDays': return item.reportingTimeFromIssueInDays ?? 0;
        case 'verificationTimeFromReportingInDays': return item.verificationTimeFromReportingInDays ?? 0;
        case 'totalBequestCount': return item.totalBequestCount ?? 0;
        case 'pecuniaryBequestCount': return item.pecuniaryBequestCount ?? 0;
        case 'residualBequestCount': return item.residualBequestCount ?? 0;
        case 'effectBequestCount': return item.effectBequestCount ?? 0;
        case 'hadsBequestCount': return item.hadsBequestCount ?? 0;
        case 'aiCharitableCount': return item.aiCharitableCount ?? 0;
        case 'reportedClientCharitableCount': return item.reportedClientCharitableCount ?? 0;
        case 'reportedNonClientCharitableCount': return item.reportedNonClientCharitableCount ?? 0;
        case 'namedSubscriptionCountThatThisTriggers': return item.namedSubscriptionCountThatThisTriggers;
        case 'csvNamesOfNamedSubscriptionCountThatThisTriggers': return item.csvNamesOfNamedSubscriptionCountThatThisTriggers;
        default: return '';
      }
    };
  }

  async loadData() {
    this.isLoading = true;
    try {
      const params = new HttpParams()
        .set('user', this.loggedInUserTag)
        .set('dateFrom', this.toIsoDate(this.dateFrom))
        .set('dateTo', this.toIsoDate(this.dateTo));

      this.allRecords = await lastValueFrom(
        this.http.get<ProbateStatusItem[]>('/charitycentral/GetProbateStatusReport', { params })
      );
      this.dataSource.data = this.allRecords;
      if (this.paginator) this.paginator.firstPage();
    } catch {
      this.toastr.error('Failed to load probate status report.', '', { timeOut: 4000 });
    } finally {
      this.isLoading = false;
    }
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-GB');
  }

  downloadCsv() {
    const rows = this.dataSource.data;
    if (!rows.length) {
      this.toastr.info('No data to download.', '', { timeOut: 3000 });
      return;
    }

    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const header = [
      'Status', 'Deceased Name', 'Probate ID', 'Date of Probate',
      'Current Owner', 'Reporting User', 'Reporting Complete',
      'Verification User', 'Verification Complete',
      'First Alerted', 'First Invoiced',
      'Age (Days)', 'Reporting Time (Days)', 'Verification Time (Days)',
      'Total Bequests', 'Pecuniary', 'Residual', 'Effect', 'HADS',
      'AI Charitable Count', 'Reported Client Charitable Count', 'Reported Non-Client Charitable Count',
      'Named Sub Count', 'Named Subscriptions'
    ].join(',');

    const lines = rows.map(r => [
      esc(r.probateStatus),
      esc(r.deceasedName),
      esc(r.probateId),
      esc(this.formatDate(r.dateOfProbate)),
      esc(r.currentOwner),
      esc(r.reportingUser),
      esc(this.formatDate(r.reportingCompleteDate)),
      esc(r.verificationUser),
      esc(this.formatDate(r.verificationCompleteDate)),
      esc(this.formatDate(r.firstClientEmailedDate)),
      esc(this.formatDate(r.firstInvoiceCreatedDate)),
      esc(r.ageInDays),
      esc(r.reportingTimeFromIssueInDays),
      esc(r.verificationTimeFromReportingInDays),
      esc(r.totalBequestCount),
      esc(r.pecuniaryBequestCount),
      esc(r.residualBequestCount),
      esc(r.effectBequestCount),
      esc(r.hadsBequestCount),
      esc(r.aiCharitableCount),
      esc(r.reportedClientCharitableCount),
      esc(r.reportedNonClientCharitableCount),
      esc(r.namedSubscriptionCountThatThisTriggers),
      esc(r.csvNamesOfNamedSubscriptionCountThatThisTriggers)
    ].join(','));

    const csv = [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `probate_status_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private toIsoDate(d: any): string {
    if (typeof d.format === 'function') {
      return d.format('YYYY-MM-DD');
    }
    const year = (d as Date).getFullYear();
    const month = ((d as Date).getMonth() + 1).toString().padStart(2, '0');
    const day = (d as Date).getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
